import type { ApolloClient, NormalizedCacheObject } from "@apollo/client";
import { ApolloProvider } from "@apollo/client";
import { createGenerateClassName, StylesProvider } from "@material-ui/core/styles";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";

import { WindowSizeProvider } from "@eroad/ui-library/WindowSizeProvider";
import { AlertProvider } from "@phoenix/context/alertContext";
import { AppProvider } from "@phoenix/context/appContext";
import { MobileProvider } from "@phoenix/context/mobileContext";
import { UserProvider } from "@phoenix/context/userContext";
import { InspectorProvider } from "@phoenix/domains/tracking/context/inspectorContext";
import GraphHost from "@phoenix/domains/tracking/GraphHost";

import { PhoenixLocaleProvider } from "./PhoenixLocaleProvider";
import { ThemeProvider } from "./themeProvider/ThemeProvider";

export interface PhoenixProvidersProps {
  children?: React.ReactNode;
}

const PhoenixProviders = ({ children }: PhoenixProvidersProps) => {
  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        refetchOnWindowFocus: false,
      },
    },
  });

  const generateClassName = createGenerateClassName({
    productionPrefix: "cl",
    disableGlobal: true,
  });

  return (
    <>
      <QueryClientProvider client={queryClient}>
        {process.env.NODE_ENV !== "production" && process.env.NODE_ENV !== "test" ? (
          <ReactQueryDevtools initialIsOpen={false} />
        ) : null}
        <ApolloProvider client={GraphHost.getInstance() as ApolloClient<NormalizedCacheObject>}>
          <AppProvider>
            <UserProvider>
              <AlertProvider>
                <InspectorProvider>
                  <StylesProvider generateClassName={generateClassName}>
                    <ThemeProvider>
                      <MobileProvider>
                        <PhoenixLocaleProvider>
                          <WindowSizeProvider>{children}</WindowSizeProvider>
                        </PhoenixLocaleProvider>
                      </MobileProvider>
                    </ThemeProvider>
                  </StylesProvider>
                </InspectorProvider>
              </AlertProvider>
            </UserProvider>
          </AppProvider>
        </ApolloProvider>
      </QueryClientProvider>
    </>
  );
};

export { PhoenixProviders };
